const tds = require('epc-tds');

/**
 * SGTIN-96 encoder (the inverse of `gs1.parse` for the SGTIN scheme).
 *
 * Builds a canonical SGTIN-96 EPC hex from a GS1 company prefix, GTIN, and serial. Used by Serialized
 * Each model+quantity auto-serialization to mint a *real* GS1 tag when the tenant is GS1-configured
 * with a company prefix and the model carries a GTIN — falling back to the `_id`/A0 strategy otherwise
 * (PRD §3.4.2).
 *
 * Why the company prefix is required separately: an SGTIN splits the GTIN into a company-prefix segment
 * and an item-reference segment, and where that split lands (the "partition") depends on the GS1
 * company-prefix *length* — which is assigned by GS1 and is NOT derivable from the GTIN digits alone.
 * The caller supplies it (a tenant setting).
 *
 * Throws on any invalid or insufficient input so the caller can cleanly fall back rather than mint a
 * corrupt tag. (epc-tds `setGtin` silently produces garbage for a too-short GTIN, so length is checked
 * here explicitly.)
 */

// SGTIN-96 carries a 38-bit serial field.
const SGTIN96_MAX_SERIAL = 274877906943; // 2^38 - 1

/**
 * @param {Object} params
 * @param {string|number} params.companyPrefix - GS1 company prefix (6–12 digits); its LENGTH sets the
 *   partition, so leading zeros are significant — pass it 0-padded to its true GS1 length ('0086699').
 * @param {string|number} params.gtin - GTIN whose leading digits are the company prefix (GTIN-13/14).
 * @param {number} params.serial - item serial (0 … 2^38-1).
 * @param {number} [params.filter=1] - SGTIN filter value (1 = POS item / retail consumer trade item).
 * @returns {string} 24-char uppercase SGTIN-96 hex.
 */
function encodeSgtin({ companyPrefix, gtin, serial, filter = 1 } = {}) {
    const prefix = String(companyPrefix ?? '').trim();
    if (!/^\d{6,12}$/.test(prefix)) {
        throw new Error(`Invalid GS1 company prefix '${companyPrefix}': must be 6–12 digits.`);
    }
    const gtinStr = String(gtin ?? '').trim();
    if (!/^\d+$/.test(gtinStr)) {
        throw new Error(`Invalid GTIN '${gtin}': must be numeric.`);
    }

    const partition = 12 - prefix.length;
    // setGtin consumes the first (company digits + item-reference digits) characters of the GTIN;
    // a shorter GTIN would be padded with garbage, so reject it up front.
    const partitionDef = tds.Sgtin96.PARTITIONS[partition];
    const neededDigits = partitionDef.a.digits + partitionDef.b.digits;
    if (gtinStr.length < neededDigits) {
        throw new Error(
            `GTIN '${gtin}' too short for a ${prefix.length}-digit company prefix ` +
            `(need ≥ ${neededDigits} digits).`
        );
    }

    const serialNum = Number(serial);
    if (!Number.isInteger(serialNum) || serialNum < 0 || serialNum > SGTIN96_MAX_SERIAL) {
        throw new Error(`Serial '${serial}' out of range for SGTIN-96 (0 … ${SGTIN96_MAX_SERIAL}).`);
    }

    const epc = new tds.Sgtin96();
    epc.setPartition(partition);
    epc.setFilter(Number(filter));
    epc.setGtin(gtinStr);
    epc.setSerial(serialNum);
    return epc.toHexString().toUpperCase();
}

module.exports = encodeSgtin;
module.exports.SGTIN96_MAX_SERIAL = SGTIN96_MAX_SERIAL;
